import { useQuery } from "@tanstack/react-query";
import {
  Users,
  FileText,
  Code,
  Activity,
  TrendingUp,
  TrendingDown,
  Shield,
  Key,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { adminApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";

// Stat card component
const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendValue,
  color = "blue",
}) => {
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    purple: "bg-purple-500",
    gray: "bg-gray-500",
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            )}
            {trend && (
              <div
                className={cn(
                  "flex items-center gap-1 text-sm mt-2",
                  trend === "up" ? "text-emerald-600" : "text-red-600",
                )}
              >
                {trend === "up" ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={cn("p-3 rounded-full", colorClasses[color])}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  // Query: Fetch dashboard stats
  const {
    data: stats,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => {
      const response = await adminApi.getDashboard();
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(
        response.data?.error?.message || "Failed to load dashboard",
      );
    },
  });

  const handleRefresh = () => {
    refetch();
  };

  // Check if user is the app owner
  if (!user?.isOwner) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t("errors.accessDenied")}
            </h2>
            <p className="text-muted-foreground">
              This page is only accessible to the app owner.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t("errors.loadingDashboard")}
            </h2>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const userStats = stats?.stats?.users || {};
  const envelopeStats = stats?.stats?.envelopes || {};
  const embedStats = stats?.stats?.embed || {};
  const apiKeyStats = stats?.stats?.apiKeys || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("admin.dashboard.title")}
          </h1>
          <p className="text-muted-foreground">
            System overview and management
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          {t("common.refresh")}
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t("admin.dashboard.totalUsers")}
          value={parseInt(userStats.total || 0).toLocaleString()}
          subtitle={t("admin.dashboard.newThisWeek", {
            count: userStats.new_week || 0,
          })}
          icon={Users}
          color="blue"
        />
        <StatCard
          title={t("admin.dashboard.activeUsers")}
          value={parseInt(userStats.active || 0).toLocaleString()}
          subtitle={t("admin.dashboard.activeToday", {
            count: userStats.active_today || 0,
          })}
          icon={Activity}
          color="green"
        />
        <StatCard
          title={t("admin.dashboard.totalEnvelopes")}
          value={parseInt(envelopeStats.total || 0).toLocaleString()}
          subtitle={t("admin.dashboard.completed", {
            count: envelopeStats.completed || 0,
          })}
          icon={FileText}
          color="purple"
        />
        <StatCard
          title={t("admin.dashboard.embedSessions")}
          value={parseInt(embedStats.total_sessions || 0).toLocaleString()}
          subtitle={t("admin.dashboard.today", {
            count: embedStats.sessions_today || 0,
          })}
          icon={Code}
          color="amber"
        />
      </div>

      {/* Feature Stats */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("admin.dashboard.userStatus")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm">{t("admin.dashboard.active")}</span>
                </div>
                <Badge variant="outline">{userStats.active || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">
                    {t("admin.pendingVerification")}
                  </span>
                </div>
                <Badge variant="outline">{userStats.pending || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm">{t("common.suspended")}</span>
                </div>
                <Badge variant="outline">{userStats.suspended || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("analytics.featureAdoption")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">{t("embed.enabled")}</span>
                </div>
                <Badge variant="outline">{userStats.embed_enabled || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-500" />
                  <span className="text-sm">{t("settings.whiteLabel")}</span>
                </div>
                <Badge variant="outline">
                  {userStats.white_label_enabled || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">2FA Enabled</span>
                </div>
                <Badge variant="outline">
                  {userStats.two_factor_enabled || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("admin.dashboard.envelopeStatus")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{t("common.draft")}</span>
                </div>
                <Badge variant="outline">{envelopeStats.draft || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">{t("common.sent")}</span>
                </div>
                <Badge variant="outline">{envelopeStats.sent || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm">{t("common.completed")}</span>
                </div>
                <Badge variant="outline">{envelopeStats.completed || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.dashboard.quickActions")}</CardTitle>
          <CardDescription>
            {t("admin.dashboard.quickActionsDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Link to="/admin/users">
              <Button
                variant="outline"
                className="w-full justify-between h-auto py-4"
              >
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5" />
                  <div className="text-left">
                    <p className="font-medium">
                      {t("admin.dashboard.manageUsers")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("admin.dashboard.manageUsersDesc")}
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>

            <Link to="/admin/users?embedEnabled=false">
              <Button
                variant="outline"
                className="w-full justify-between h-auto py-4"
              >
                <div className="flex items-center gap-3">
                  <Code className="h-5 w-5" />
                  <div className="text-left">
                    <p className="font-medium">{t("embed.enable")}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("reminders.forPendingUsers")}
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>

            <Link to="/admin/users?status=pending_verification">
              <Button
                variant="outline"
                className="w-full justify-between h-auto py-4"
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5" />
                  <div className="text-left">
                    <p className="font-medium">{t("admin.pendingUsers")}</p>
                    <p className="text-xs text-muted-foreground">
                      {userStats.pending || 0} awaiting verification
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>

            <Link to="/admin/users?status=suspended">
              <Button
                variant="outline"
                className="w-full justify-between h-auto py-4"
              >
                <div className="flex items-center gap-3">
                  <XCircle className="h-5 w-5" />
                  <div className="text-left">
                    <p className="font-medium">{t("admin.suspendedUsers")}</p>
                    <p className="text-xs text-muted-foreground">
                      {userStats.suspended || 0} accounts
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* API Keys Stats */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("common.apiKeys")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t("admin.totalKeys")}
                </span>
                <span className="font-medium">{apiKeyStats.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t("apiKeys.activeKeys")}
                </span>
                <span className="font-medium">{apiKeyStats.active || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t("admin.usedToday")}
                </span>
                <span className="font-medium">
                  {apiKeyStats.used_today || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("embed.sessions7Days")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t("common.created")}
                </span>
                <span className="font-medium">
                  {embedStats.sessions_week || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t("common.completed")}
                </span>
                <span className="font-medium">
                  {embedStats.completed_sessions || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t("common.activeNow")}
                </span>
                <span className="font-medium">
                  {embedStats.active_sessions || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
